import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import type { ReviewBlock, ReviewDetails, ReviewOutlineNode } from './adapter/types';
import { PremiumBadge } from './PremiumBadge';
import { SidebarDetailsTab } from './SidebarDetailsTab';
import { SidebarEditTab } from './SidebarEditTab';
import { type SignatureField } from './SignatureFieldOverlay';
import { type Signatory, type SignatoryEvent } from './utils';

interface ReviewSidebarProps {
  sidebarTab: string;
  onTabChange: (tab: string) => void;
  isEditable: boolean;
  isEditDisabled?: boolean;
  isPremiumUnlocked: boolean;
  editedBlockIds: Set<string>;
  isFixedDocx?: boolean;

  // Details tab
  details: ReviewDetails;
  signatureFields: SignatureField[];
  numPages: number | null;
  signatories: Signatory[];
  signatoryEventByEmail: Map<string, SignatoryEvent>;
  currentPage: number;
  onPageClick: (page: number) => void;


  // Edit tab
  editTabScrollRef: React.Ref<HTMLDivElement>;
  isEditing: boolean;
  editingBlock: ReviewBlock | null;
  isSavingEdit: boolean;
  isEditingEffectiveDate: boolean;
  onSaveEdit: (newText: string) => Promise<void>;
  onCancelEdit: () => void;
  onEditorDirtyChange: (dirty: boolean) => void;
  onEditorTextChange: (text: string) => void;
  outline: ReviewOutlineNode[];
  onEditBlock: (block: ReviewBlock) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (id: string, open: boolean) => void;
}

const SCROLLBAR_CLASSES =
  '[scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent';

/**
 * Tabbed body of the review sidebar (shared between the desktop aside and the
 * mobile Sheet): a "Details" tab and, when the document is editable, an "Edit"
 * tab. The footer with Send/Download actions is rendered separately by the
 * caller so it can be hidden while the block editor is open.
 */
export function ReviewSidebar({
  sidebarTab,
  onTabChange,
  isEditable,
  isEditDisabled = false,
  isPremiumUnlocked,
  editedBlockIds,
  details,
  signatureFields,
  numPages,
  signatories,
  signatoryEventByEmail,
  currentPage,
  onPageClick,
  editTabScrollRef,
  isEditing,
  editingBlock,
  isSavingEdit,
  isEditingEffectiveDate,
  onSaveEdit,
  onCancelEdit,
  onEditorDirtyChange,
  onEditorTextChange,
  outline,
  onEditBlock,
  openSections,
  isFixedDocx = false,
  onToggleSection,
}: ReviewSidebarProps) {
  const { t } = useTranslation();

  return (
    <Tabs value={sidebarTab} onValueChange={onTabChange} className="flex flex-col flex-1 min-h-0">
      <TabsList className="mx-4 mt-3 w-auto ">
        <TabsTrigger value="details" className="flex-1">
          {t('GENERATEREVIEW_TABS_DETAILS', { defaultValue: 'Details' })}
        </TabsTrigger>
        {isEditable && (
          <TabsTrigger value="edit" className="flex-1" disabled={isEditDisabled}>
            {t('GENERATEREVIEW_TABS_EDIT', { defaultValue: 'Edit' })}
            {editedBlockIds.size > 0 ? (
              <span className="ml-1.5 text-[10px] flex items-center justify-center size-4 font-medium text-selise-blue-500 bg-selise-blue-500/15 rounded-full px-1.5 py-0.5">
                <span>{editedBlockIds.size}</span>
              </span>
            ) : (
              <PremiumBadge unlocked={isPremiumUnlocked} />
            )}
          </TabsTrigger>
        )}
      </TabsList>

      {/* Details */}
      <TabsContent
        value="details"
        className={`flex-1 overflow-y-auto min-h-0 p-4 space-y-6 ${SCROLLBAR_CLASSES}`}
      >
        <SidebarDetailsTab
          details={details}
          signatureFields={signatureFields}
          numPages={numPages}
          signatories={signatories}
          signatoryEventByEmail={signatoryEventByEmail}
          currentPage={currentPage}
          onPageClick={onPageClick}
          isFixedDocx={isFixedDocx}
        />
      </TabsContent>

      {/* Edit */}
      {isEditable && (
        <TabsContent
          ref={editTabScrollRef}
          value="edit"
          className={`flex-1 overflow-y-auto min-h-0 ${SCROLLBAR_CLASSES}`}
        >
          <SidebarEditTab
            isEditing={isEditing}
            editingBlock={editingBlock}
            isSavingEdit={isSavingEdit}
            isEditingEffectiveDate={isEditingEffectiveDate}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
            onDirtyChange={onEditorDirtyChange}
            onTextChange={onEditorTextChange}
            outline={outline}
            editedBlockIds={editedBlockIds}
            onEditBlock={onEditBlock}
            openSections={openSections}
            onToggleSection={onToggleSection}
          />
        </TabsContent>
      )}
    </Tabs>
  );
}
